/**
 * Recorders read raw frames and audio blocks with MediaStreamTrackProcessor. Chrome and Edge have it on the
 * page. Safari has it only inside workers, and mediabunny's own fallbacks don't hold up there: video is
 * handed to a worker in a way Safari rejects, and audio goes through a new AudioContext per recorder, which
 * Safari keeps suspended unless it is created during a click, so recordings started by the host's
 * command (a network message, not a click) began late or never.
 *
 * On those browsers this installs a page-level MediaStreamTrackProcessor:
 *  - video: the track is transferred to a worker that runs the real processor and posts frames back;
 *  - audio: one shared AudioContext, unlocked by the user's Join click, taps the mic with an AudioWorklet.
 * Mediabunny then takes the same path it uses in Chrome.
 */

let audioContext: AudioContext | null = null;
let workletReady: Promise<void> | null = null;
let installed = false;

/** Call from a click handler (Join, test recording): Safari only lets audio start inside a user gesture. */
export function unlockAudio() {
  if (!installed) return;
  const ctx = sharedAudioContext();
  if (ctx.state !== 'running') void ctx.resume().catch(() => {});
}

function sharedAudioContext() {
  if (!audioContext) {
    audioContext = new AudioContext({ sampleRate: 48000, latencyHint: 'interactive' });
    (window as any).__takehouseAudio = audioContext; // for diagnostics
    // Re-arm on any later click if the browser suspended it (e.g. after the machine slept).
    const kick = () => {
      if (audioContext && audioContext.state !== 'running') void audioContext.resume().catch(() => {});
    };
    addEventListener('pointerdown', kick, true);
    addEventListener('keydown', kick, true);
  }
  return audioContext;
}

/**
 * iPhone cameras deliver landscape sensor frames marked with a rotation (VideoFrame.rotation, and sometimes flip)
 * for the picture to be shown upright. The live call honours the mark; the recorder's library ignores it, so
 * portrait recordings came out sideways. upright() redraws such frames the right way up. Browsers differ on
 * whether drawImage applies the mark itself, so that is measured once instead of assumed.
 */
export const UPRIGHT_SOURCE = `
  let drawHonoursRotation = null;
  function probeRotation() {
    try {
      const src = new OffscreenCanvas(4, 2);
      const g = src.getContext('2d');
      g.fillStyle = '#f00'; g.fillRect(0, 0, 2, 2);
      g.fillStyle = '#00f'; g.fillRect(2, 0, 2, 2);
      const vf = new VideoFrame(src, { timestamp: 0, rotation: 90 });
      if (vf.rotation !== 90) { vf.close(); return false; }
      const dst = new OffscreenCanvas(2, 4);
      const d = dst.getContext('2d');
      d.drawImage(vf, 0, 0, 2, 4);
      vf.close();
      // Turned clockwise, the left (red) half is on top and the right (blue) half at the bottom.
      const bottom = d.getImageData(0, 3, 1, 1).data;
      return bottom[2] > bottom[0];
    } catch { return false; }
  }
  let canvas = null, ctx = null;
  function upright(frame) {
    const r = frame.rotation || 0, flip = !!frame.flip;
    if (!r && !flip) return frame;
    if (drawHonoursRotation === null) drawHonoursRotation = probeRotation();
    // The stored picture's size, before rotation (browsers disagree on whether displayWidth is already turned).
    const w = frame.visibleRect ? frame.visibleRect.width : frame.codedWidth;
    const h = frame.visibleRect ? frame.visibleRect.height : frame.codedHeight;
    const ow = r % 180 ? h : w, oh = r % 180 ? w : h;
    if (!canvas || canvas.width !== ow || canvas.height !== oh) { canvas = new OffscreenCanvas(ow, oh); ctx = canvas.getContext('2d'); }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    if (drawHonoursRotation) {
      ctx.drawImage(frame, 0, 0, ow, oh);
    } else {
      ctx.translate(ow / 2, oh / 2);
      ctx.rotate((r * Math.PI) / 180);
      if (flip) ctx.scale(-1, 1);
      ctx.drawImage(frame, -w / 2, -h / 2, w, h);
    }
    const out = new VideoFrame(canvas, { timestamp: frame.timestamp, duration: frame.duration ?? undefined });
    frame.close();
    return out;
  }
`;

const WORKER_SOURCE = `
  ${UPRIGHT_SOURCE}
  const readers = new Map();
  self.postMessage({ type: 'support', supported: typeof MediaStreamTrackProcessor !== 'undefined' });
  self.onmessage = async (e) => {
    const m = e.data;
    if (m.type === 'start') {
      try {
        const reader = new MediaStreamTrackProcessor({ track: m.track }).readable.getReader();
        readers.set(m.id, { reader, track: m.track });
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          let frame = value;
          try { frame = upright(value); } catch (err) { if (!self.uprightFailed) { self.uprightFailed = true; console.warn('[takehouse] could not turn a rotated frame upright: ' + (err && err.message || err)); } }
          self.postMessage({ type: 'frame', id: m.id, frame }, [frame]);
        }
        self.postMessage({ type: 'done', id: m.id });
      } catch (err) {
        self.postMessage({ type: 'error', id: m.id, message: String(err && err.message || err) });
      }
    } else if (m.type === 'stop') {
      const r = readers.get(m.id);
      readers.delete(m.id);
      if (r) { r.reader.cancel().catch(() => {}); r.track.stop(); }
    }
  };
`;

const WORKLET_SOURCE = `
  // Collects 256-frame blocks (~5 ms: small, so arrival-time placement stays tight) and posts them with the block's position in the context's sample clock.
  class Tap extends AudioWorkletProcessor {
    constructor() { super(); this.block = 256; this.fill = 0; this.start = 0; this.planes = null; }
    process(inputs) {
      const input = inputs[0];
      if (!input || !input.length) return true;
      if (!this.planes || this.planes.length !== input.length) {
        this.planes = input.map(() => new Float32Array(this.block)); this.fill = 0;
      }
      if (this.fill === 0) this.start = currentFrame;
      const n = input[0].length;
      for (let c = 0; c < input.length; c++) this.planes[c].set(input[c], this.fill);
      this.fill += n;
      if (this.fill >= this.block) {
        const planes = this.planes;
        this.port.postMessage({ start: this.start, planes }, planes.map((p) => p.buffer));
        this.planes = input.map(() => new Float32Array(this.block)); this.fill = 0;
      }
      return true;
    }
  }
  registerProcessor('takehouse-tap', Tap);
`;

let worker: Worker | null = null;
let nextId = 1;

/** Frames thrown away because the recorder was behind: a direct sign the computer can't keep up. */
export const pipelineStats = { droppedVideoFrames: 0 };
/** performance.now() when each recorder's track got its first frame or block from this shim (timing diagnostics). */
export const firstDelivery = new WeakMap<MediaStreamTrack, number>();
/**
 * Audio blocks from this shim whose timestamp is their true capture time (performance.now() clock, in µs), read
 * off the audio clock rather than their arrival: a busy page holds blocks back and delivers them in bursts,
 * 100+ ms late. The recorder places such blocks by this time (see recorder.ts, continuousAudio).
 */
export const wallClockedAudio = new WeakSet<AudioData>();
/** Which shared audio input each recorder's track was given (diagnostics: two recorders of one mic must share). */
export const tapOf = new WeakMap<MediaStreamTrack, string>();

/** Per recorder's video track: the longest any frame waited between arriving and being read (diagnostics). */
export const videoWaitMaxMs = new WeakMap<MediaStreamTrack, number>();
/**
 * Per recorder's video track: how much later than the promptest frame each frame was read, against its capture
 * time. The recorder places a frame by when it reads it, so this is how late the picture lands. (Frame timestamps
 * on a cloned track don't share the page's clock, so only differences count: capture-to-read minus the smallest
 * capture-to-arrival seen.) Diagnostics: { max, avg }.
 */
export const videoAgeMs = new WeakMap<MediaStreamTrack, { max: number; sum: number; n: number; minArrival: number }>();
/** Frames waiting for the recorder, at most: a few, so a slow encoder costs frames, never delay. */
const VIDEO_QUEUE = 3;

/**
 * Camera frames for a recorder, newest first served. When the encoder falls behind (two cameras on one Mac in
 * Safari), the oldest waiting frames are dropped. The recorder stamps each frame when it reads it, so holding a
 * queue of 30 and dropping the newest had every frame wait a full second: the picture ran 1 s behind the sound.
 */
function videoReadable(track: MediaStreamTrack): ReadableStream<VideoFrame> {
  const id = nextId++;
  const w = worker!;
  let onMessage: (e: MessageEvent) => void;
  const queue: { frame: VideoFrame; at: number }[] = [];
  let waiting: ((v: { frame: VideoFrame; at: number } | null) => void) | null = null;
  let finished = false;
  let failure: Error | null = null;
  const serve = (item: { frame: VideoFrame; at: number } | null) => {
    const resolve = waiting;
    waiting = null;
    resolve?.(item);
  };
  return new ReadableStream<VideoFrame>(
    {
      start() {
        onMessage = (e) => {
          const m = e.data;
          if (m.id !== id) return;
          if (m.type === 'frame') {
            if (!firstDelivery.has(track)) firstDelivery.set(track, performance.now());
            const item = { frame: m.frame as VideoFrame, at: performance.now() };
            const a = videoAgeMs.get(track) ?? { max: 0, sum: 0, n: 0, minArrival: Infinity };
            a.minArrival = Math.min(a.minArrival, item.at - item.frame.timestamp / 1000);
            videoAgeMs.set(track, a);
            if (waiting) return serve(item);
            queue.push(item);
            while (queue.length > VIDEO_QUEUE) {
              queue.shift()!.frame.close();
              pipelineStats.droppedVideoFrames++;
            }
          } else if (m.type === 'done' || m.type === 'error') {
            w.removeEventListener('message', onMessage);
            finished = true;
            if (m.type === 'error') failure = new Error(m.message);
            serve(null);
          }
        };
        w.addEventListener('message', onMessage);
        // Transfer, never clone: Safari refuses to copy a MediaStreamTrack. Recorders always pass their own clone.
        w.postMessage({ type: 'start', id, track }, [track as unknown as Transferable]);
      },
      async pull(controller) {
        const item = queue.shift() ?? (finished ? null : await new Promise<{ frame: VideoFrame; at: number } | null>((r) => (waiting = r)));
        if (!item) {
          if (failure) controller.error(failure);
          else controller.close();
          return;
        }
        const now = performance.now();
        const waited = now - item.at;
        if (waited > (videoWaitMaxMs.get(track) ?? 0)) videoWaitMaxMs.set(track, Math.round(waited));
        const a = videoAgeMs.get(track);
        if (a && Number.isFinite(a.minArrival)) {
          const late = now - item.frame.timestamp / 1000 - a.minArrival;
          a.max = Math.max(a.max, late);
          a.sum += late;
          a.n++;
        }
        controller.enqueue(item.frame);
      },
      cancel() {
        w.removeEventListener('message', onMessage);
        w.postMessage({ type: 'stop', id });
        for (const q of queue.splice(0)) q.frame.close();
      },
    },
    // Nothing is held inside the stream itself; frames wait in `queue`, where the oldest can be dropped.
    new CountQueuingStrategy({ highWaterMark: 0 }),
  );
}

/**
 * One audio input per microphone, shared by everything that records it (the WAV and the camera file's scratch
 * audio). Each MediaStreamAudioSourceNode buffers by its own amount, so separate inputs put the same sound
 * 15–20 ms apart in different files; one input hands every block to every recorder at the same moment.
 * A tap also stays open between takes (see warmAudioTaps), so a take started by the host's command gets audio
 * from its first block instead of waiting for a new input to spin up.
 */
type Block = { start: number; planes: Float32Array[] };
interface AudioTap {
  key: string;
  /** Blocks delivered so far (diagnostics). */
  blocks: number;
  users: Set<(b: Block | null) => void>;
  idleTimer: ReturnType<typeof setTimeout> | null;
  close: () => void;
  /** performance.now() ms at which audio-clock frame `frame` was produced (arrival delays removed). */
  wallMs: (frame: number) => number;
  /** Recorders reading from this input; while any are, the clock mapping stays fixed (see wallMs). */
  recorders: number;
  lockedOffset: number | null;
}
const taps = new Map<string, AudioTap>();
const tapSerial = new WeakMap<AudioTap, number>();
let tapsOpened = 0;
(globalThis as any).__takehouseTaps = taps; // for diagnostics
const TAP_IDLE_MS = 20_000;

/** Same device with the same processing = the same sound. */
function tapKey(track: MediaStreamTrack) {
  const s = track.getSettings();
  return [s.deviceId || track.label, s.echoCancellation, s.noiseSuppression, s.autoGainControl, s.channelCount].join('|');
}

function openTap(track: MediaStreamTrack): AudioTap {
  const key = tapKey(track);
  const existing = taps.get(key);
  if (existing) return existing;
  const ctx = sharedAudioContext();
  // The tap owns its own clone, so it lives as long as anyone is recording, whichever clone they hold.
  let input = track.clone();
  let node: AudioWorkletNode | null = null;
  let source: MediaStreamAudioSourceNode | null = null;
  let sink: GainNode | null = null;
  let closed = false;
  // Arrival minus audio-clock time for recent blocks. The smallest of the last few seconds is a block that
  // wasn't held up, so it maps the audio clock onto performance.now() without the page's delays (and tracks
  // the slow drift between the two clocks).
  const recent: { at: number; delta: number }[] = [];
  const currentOffset = () => (recent.length ? Math.min(...recent.map((r) => r.delta)) : 0);
  const tap: AudioTap = {
    key,
    blocks: 0,
    recorders: 0,
    lockedOffset: null,
    // While recorders are reading, one mapping for all of them, taken from the calm seconds before they started.
    // Letting it follow every new least-delayed block placed two files of one take a few ms apart when one
    // started later than the other (8 ms seen in Safari), and read small mapping changes as gaps.
    wallMs: (frame) => (frame / ctx.sampleRate) * 1000 + (tap.lockedOffset ?? currentOffset()),
    users: new Set(),
    idleTimer: null,
    close() {
      if (closed) return;
      closed = true;
      taps.delete(key);
      source?.disconnect();
      node?.disconnect();
      sink?.disconnect();
      if (node) node.port.onmessage = null;
      input.stop();
      node = source = sink = null;
      for (const u of tap.users) u(null);
      tap.users.clear();
    },
  };
  taps.set(key, tap);
  tapSerial.set(tap, ++tapsOpened);
  const connectInput = () => {
    source?.disconnect();
    source = ctx.createMediaStreamSource(new MediaStream([input]));
    source.connect(node!);
  };
  input.addEventListener('ended', () => tap.close());
  void (async () => {
    await workletReady;
    if (closed) return;
    // A fixed channel count: the audio graph up- or down-mixes, so blocks never change shape mid-take.
    const channelCount = Math.min(2, Math.max(1, track.getSettings().channelCount ?? 1));
    node = new AudioWorkletNode(ctx, 'takehouse-tap', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount,
      channelCountMode: 'explicit',
      channelInterpretation: 'speakers',
    });
    // A silent path to the speakers keeps the graph running in every browser.
    sink = ctx.createGain();
    sink.gain.value = 0;
    node.connect(sink).connect(ctx.destination);
    // Safari sometimes leaves a mic input that was attached while the context was still starting
    // permanently silent, so attach only once it runs.
    if (ctx.state !== 'running') {
      console.warn('[takehouse] audio is waiting for a click to start (browser autoplay rule)');
      await new Promise<void>((resolve) => {
        const onChange = () => {
          if (ctx.state !== 'running') return;
          ctx.removeEventListener('statechange', onChange);
          resolve();
        };
        ctx.addEventListener('statechange', onChange);
      });
    }
    if (closed) return;
    connectInput();
    // A real microphone is never digitally silent (hard mute is applied later, in the recorder). Exact zeros
    // from a live, unmuted track mean the input path broke: rebuild it from a fresh clone of the track.
    let silentFrames = 0;
    let repairs = 0;
    node.port.onmessage = (e) => {
      const block = e.data as Block;
      const frames = block.planes[0].length;
      const now = performance.now();
      recent.push({ at: now, delta: now - ((block.start + frames) / ctx.sampleRate) * 1000 });
      while (recent.length && now - recent[0].at > 3000) recent.shift();
      const silent = block.planes.every((p) => p.every((v) => v === 0));
      silentFrames = silent && input.readyState === 'live' && !input.muted && tap.users.size ? silentFrames + frames : 0;
      if (silentFrames >= 2 * ctx.sampleRate && repairs === 2) {
        // Reconnecting hasn't helped: tell the person rather than record nothing.
        dispatchEvent(new CustomEvent('takehouse:mic-silent', { detail: { label: track.label } }));
      }
      if (silentFrames >= 2 * ctx.sampleRate && repairs < 5) {
        repairs++;
        silentFrames = 0;
        console.warn(`[takehouse] microphone input went silent; reconnecting (attempt ${repairs})`);
        try {
          const fresh = input.clone();
          input.stop();
          input = fresh;
          input.addEventListener('ended', () => tap.close());
        } catch {}
        connectInput();
      }
      tap.blocks++;
      for (const u of tap.users) u(block);
    };
  })().catch((e) => console.error('[takehouse] microphone input could not start', e));
  return tap;
}

function joinTap(track: MediaStreamTrack, user: (b: Block | null) => void) {
  const tap = openTap(track);
  if (tap.idleTimer) clearTimeout(tap.idleTimer);
  tap.idleTimer = null;
  tap.users.add(user);
  return () => {
    tap.users.delete(user);
    if (tap.users.size || tap.idleTimer) return;
    tap.idleTimer = setTimeout(() => {
      tap.idleTimer = null;
      if (!tap.users.size) tap.close();
    }, TAP_IDLE_MS);
  };
}

const warm = new Map<string, () => void>();
/**
 * Keeps a tap open for each of these microphone tracks (and closes the rest), from the green room on: the
 * first recording, often started by the host's command rather than a click here, then starts on a running input.
 */
export function warmAudioTaps(tracks: MediaStreamTrack[]) {
  if (!installed) return;
  const want = new Map(tracks.filter((t) => t.readyState === 'live').map((t) => [t.id, t]));
  for (const [id, leave] of warm) {
    if (!want.has(id)) {
      leave();
      warm.delete(id);
    }
  }
  for (const [id, t] of want) if (!warm.has(id)) warm.set(id, joinTap(t, () => {}));
}

function audioReadable(track: MediaStreamTrack): ReadableStream<AudioData> {
  let leave: (() => void) | null = null;
  const stop = () => {
    leave?.();
    leave = null;
  };
  return new ReadableStream<AudioData>({
    start(controller) {
      const sampleRate = sharedAudioContext().sampleRate;
      const tap = openTap(track);
      tapOf.set(track, `${tap.key} #${tapSerial.get(tap) ?? 0}`);
      if (tap.recorders++ === 0) tap.lockedOffset = null;
      let counted = true;
      const release = () => {
        if (!counted) return;
        counted = false;
        if (--tap.recorders === 0) tap.lockedOffset = null;
      };
      const leaveTap = joinTap(track, (block) => {
        if (!block) {
          stop();
          try {
            controller.close();
          } catch {}
          return;
        }
        if (!firstDelivery.has(track)) firstDelivery.set(track, performance.now());
        if (tap.lockedOffset == null) tap.lockedOffset = tap.wallMs(0);
        // Every recorder gets its own copy: each closes its AudioData when done with it.
        const frames = block.planes[0].length;
        const data = new Float32Array(frames * block.planes.length);
        block.planes.forEach((p, i) => data.set(p, i * frames));
        const audio = new AudioData({
          format: 'f32-planar',
          sampleRate,
          numberOfFrames: frames,
          numberOfChannels: block.planes.length,
          timestamp: Math.round(tap.wallMs(block.start) * 1000),
          data,
        });
        wallClockedAudio.add(audio);
        controller.enqueue(audio);
      });
      leave = () => {
        leaveTap();
        release();
      };
      track.addEventListener('ended', () => {
        stop();
        try {
          controller.close();
        } catch {}
      });
    },
    cancel() {
      stop();
      track.stop();
    },
  });
}

/** Set only when testing in Chrome, which has no processor inside workers: video then uses Chrome's own. */
let nativeVideo: (new (init: { track: MediaStreamTrack }) => { readable: ReadableStream<VideoFrame> }) | null = null;

class PageTrackProcessor {
  readable: ReadableStream<VideoFrame | AudioData>;
  constructor({ track }: { track: MediaStreamTrack }) {
    if (track.kind !== 'video') this.readable = audioReadable(track);
    else this.readable = nativeVideo ? new nativeVideo({ track }).readable : videoReadable(track);
  }
}

/** Whether MediaStreamTrackProcessor on the page is ours (Safari path) rather than the browser's. */
export const pageProcessorInstalled = () => installed;

/**
 * Installs the page-level processor where it's missing but the browser can support it (Safari).
 * `force` replaces Chrome's built-in one, so this path can be tested end to end in Chrome.
 */
export async function installTrackProcessor(force = false): Promise<'native' | 'installed' | 'unavailable'> {
  const g = globalThis as any;
  if (installed) return 'installed';
  if (typeof g.MediaStreamTrackProcessor !== 'undefined' && !force) return 'native';
  if (typeof AudioData === 'undefined' || typeof AudioWorkletNode === 'undefined') return 'unavailable';
  if (force && typeof g.MediaStreamTrackProcessor !== 'undefined') {
    nativeVideo = g.MediaStreamTrackProcessor;
    return finishInstall(g);
  }
  const url = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: 'text/javascript' }));
  const w = new Worker(url);
  const supported = await new Promise<boolean>((resolve) => {
    const t = setTimeout(() => resolve(false), 3000);
    w.addEventListener(
      'message',
      (e) => {
        clearTimeout(t);
        resolve(!!e.data?.supported);
      },
      { once: true },
    );
  });
  if (!supported) {
    w.terminate();
    return 'unavailable'; // e.g. Firefox: mediabunny's own fallbacks handle it
  }
  worker = w;
  return finishInstall(g);
}

async function finishInstall(g: any): Promise<'installed'> {
  const ctx = sharedAudioContext();
  const workletUrl = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: 'text/javascript' }));
  workletReady = ctx.audioWorklet.addModule(workletUrl);
  await workletReady;
  g.MediaStreamTrackProcessor = PageTrackProcessor;
  installed = true;
  console.info('[takehouse] using the page-level track processor (Safari path)');
  return 'installed';
}
